import { forwardRef } from 'react'
import { isEmpty } from 'lodash'
import {
    duplicatePreviousOrderFieldName,
    envelopeOptionFieldName,
    findFont,
    isPersonalLetterProduct,
    isProfessionalLetterProduct,
} from '../../helpers/slug'
import { ModalStore } from '../../state/ModalStore'
import EnvelopeStyleModal from '../Modals/EnvelopeStyleModal'
import FontStyleModal from '../Modals/FontStyleModal'
import FlyerStyleModal from '../Modals/FlyerStyleModal'
import PostcardStyleModal from '../Modals/PostcardStyleModal'
import { envelopeSwatchStyle } from './envelopeStyleOptions'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const postcardStyleImageMap = {
    Blue: '/assets/products/postcard-styles/postcardstyle_blue.png',
    'Blue Houses':
        '/assets/products/postcard-styles/postcardstyle_bluehouses.png',
    Green: '/assets/products/postcard-styles/postcardstyle_green.png',
    'Green Houses':
        '/assets/products/postcard-styles/postcardstyle_greenhouses.png',
    Pink: '/assets/products/postcard-styles/postcardstyle_pink.png',
    'Pink Houses':
        '/assets/products/postcard-styles/postcardstyle_pinkhouses.png',
    'River Reeds':
        '/assets/products/postcard-styles/postcardstyle_riverreeds.png',
    'Summer Forest':
        '/assets/products/postcard-styles/postcardstyle_summerforest.png',
    Yellow: '/assets/products/postcard-styles/postcardstyle_yellow.png',
    Sunset: '/assets/products/postcard-styles/postcardstyle_sunset.png',
    'Tri-Color Houses':
        '/assets/products/postcard-styles/postcardstyle_tricolor.png',
}

const SelectCaret = ({ className = '' }) => (
    <span className={classNames('flex-shrink-0 text-gray-400', className)}>
        ▸
    </span>
)

const getDisplayValue = (
    field,
    getValues,
    defaultCustomizationTemplate,
    watchedValue,
) => {
    const currentValue = watchedValue ?? getValues?.(field.name)
    if (!isEmpty(currentValue)) return currentValue

    const defaultValue = defaultCustomizationTemplate?.[field.name]
    if (!isEmpty(defaultValue)) return defaultValue

    return field.defaultValue || field.default || ''
}

const getAppearanceFontType = (field, isLetterAppearance = false) => {
    const name = String(field?.name || '')

    if (
        name.includes('Letter Font Style') ||
        name.includes('Envelope Font Style')
    ) {
        return name.includes('Envelope Font Style') ? 'envelope' : 'letter'
    }

    // Personal Letters uses generic names for the WooCommerce letter fields.
    if (isLetterAppearance && name.endsWith('Font Style')) return 'letter'
    if (name.includes('Font Style')) return 'generic'

    return null
}

const normalizeLabel = (field, isLetterAppearance = false) => {
    const name = String(field?.name || field?.label || '')
    const fontType = getAppearanceFontType(field, isLetterAppearance)

    if (fontType === 'letter') return 'Letter Font'
    if (fontType === 'envelope') return 'Envelope Font Style'
    if (name.includes('Envelope Font Color')) return 'Envelope Font Color'
    if (
        isLetterAppearance &&
        (name === 'Font Color' || name.endsWith(' Font Color'))
    ) {
        return 'Letter Font Color'
    }
    if (name.includes('Font Style')) return 'Font style'
    if (name.includes('Font Color')) return 'Font color'
    if (
        name.includes('Envelope Style') ||
        name.includes(envelopeOptionFieldName)
    ) {
        return 'Envelope Style'
    }

    if (name.includes('Handwriting Style')) {
        return 'Handwriting style'
    }

    if (name.includes('Postcard Style')) {
        return 'Postcard style'
    }

    if (name.includes('Flyer Style')) {
        return 'Flyer style'
    }

    return name
}

const orderLetterAppearanceFields = (fields) => {
    const rankField = (field) => {
        const name = String(field?.name || '')
        if (name.includes('Letter Font Style')) {
            return 0
        }
        if (
            name.endsWith('Font Style') &&
            !name.includes('Envelope Font Style')
        ) {
            return 0
        }
        if (
            (name === 'Font Color' || name.endsWith(' Font Color')) &&
            !name.includes('Envelope Font Color')
        ) {
            return 1
        }
        if (
            name.includes('Envelope Style') ||
            name.includes(envelopeOptionFieldName)
        ) {
            return 2
        }
        if (name.includes('Envelope Font Style')) return 3
        if (name.includes('Envelope Font Color')) return 4
        return 5
    }

    return fields
        .map((field, index) => ({ field, index, rank: rankField(field) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(({ field }) => field)
}

const postcardSwatchStyle = (value) => {
    const normalized = String(value || '').toLowerCase()

    const exactMatchImage = postcardStyleImageMap[value]
    if (exactMatchImage) {
        return {
            backgroundImage: `url('${exactMatchImage}')`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
        }
    }

    if (normalized.includes('tri-color houses')) {
        return {
            background:
                "linear-gradient(#fbf3e7, #fbf3e7), url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27132%27 height=%2746%27%3E%3Crect width=%27132%27 height=%2746%27 fill=%27%23fbf3e7%27/%3E%3Cpath d=%27M7 36 V22 L21 11 L35 22 V36 Z%27 fill=%27%23ED5C2F%27/%3E%3Crect x=%2717%27 y=%2728%27 width=%278%27 height=%278%27 fill=%27%23fbf3e7%27/%3E%3Cpath d=%27M51 36 V22 L65 11 L79 22 V36 Z%27 fill=%27%23F28230%27/%3E%3Crect x=%2761%27 y=%2728%27 width=%278%27 height=%278%27 fill=%27%23fbf3e7%27/%3E%3Cpath d=%27M95 36 V22 L109 11 L123 22 V36 Z%27 fill=%27%23FAB331%27/%3E%3Crect x=%27105%27 y=%2728%27 width=%278%27 height=%278%27 fill=%27%23fbf3e7%27/%3E%3C/svg%3E')",
            backgroundRepeat: 'repeat',
            backgroundPosition: 'top left',
            backgroundSize: '132px 46px',
        }
    }
    if (normalized.includes('sunset'))
        return {
            background:
                'linear-gradient(135deg,#ff9a4d 0%,#ff5e7e 52%,#a45ad6 100%)',
        }
    if (normalized.includes('blue houses'))
        return {
            background:
                "linear-gradient(#e7f0fb, #e7f0fb), url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2746%27 height=%2740%27%3E%3Crect width=%2746%27 height=%2740%27 fill=%27%23e7f0fb%27/%3E%3Cpath d=%27M9 31 V19 L23 9 L37 19 V31 Z%27 fill=%27%233f74c9%27/%3E%3Crect x=%2720%27 y=%2724%27 width=%277%27 height=%277%27 fill=%27%23e7f0fb%27/%3E%3C/svg%3E')",
            backgroundRepeat: 'repeat',
            backgroundPosition: 'top left',
            backgroundSize: '46px 40px',
        }
    if (normalized.includes('pink houses'))
        return {
            background:
                "linear-gradient(#fde9f1, #fde9f1), url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2746%27 height=%2740%27%3E%3Crect width=%2746%27 height=%2740%27 fill=%27%23fde9f1%27/%3E%3Cpath d=%27M9 31 V19 L23 9 L37 19 V31 Z%27 fill=%27%23e06090%27/%3E%3Crect x=%2720%27 y=%2724%27 width=%277%27 height=%277%27 fill=%27%23fde9f1%27/%3E%3C/svg%3E')",
            backgroundRepeat: 'repeat',
            backgroundPosition: 'top left',
            backgroundSize: '46px 40px',
        }
    if (normalized.includes('green houses'))
        return {
            background:
                "linear-gradient(#e8f5ec, #e8f5ec), url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2746%27 height=%2740%27%3E%3Crect width=%2746%27 height=%2740%27 fill=%27%23e8f5ec%27/%3E%3Cpath d=%27M9 31 V19 L23 9 L37 19 V31 Z%27 fill=%27%2346a86b%27/%3E%3Crect x=%2720%27 y=%2724%27 width=%277%27 height=%277%27 fill=%27%23e8f5ec%27/%3E%3C/svg%3E')",
            backgroundRepeat: 'repeat',
            backgroundPosition: 'top left',
            backgroundSize: '46px 40px',
        }
    if (normalized === 'blue')
        return {
            background: 'linear-gradient(135deg,#3a82e0 0%,#1b4f9c 100%)',
        }
    if (normalized === 'pink')
        return {
            background: 'linear-gradient(135deg,#f573a6 0%,#d2356f 100%)',
        }
    if (normalized === 'green')
        return {
            background: 'linear-gradient(135deg,#46b877 0%,#1f7e49 100%)',
        }
    if (normalized.includes('river reeds'))
        return {
            background:
                "linear-gradient(#e8f2e2, #e8f2e2), url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2746%27 height=%2748%27%3E%3Crect width=%2746%27 height=%2748%27 fill=%27%23e8f2e2%27/%3E%3Cpath d=%27M11 48 C7 33 15 29 11 15 C8 8 11 5 12 1%27 stroke=%27%235f9152%27 stroke-width=%272.4%27 fill=%27none%27 stroke-linecap=%27round%27/%3E%3Cpath d=%27M24 48 C20 33 28 29 24 15 C21 8 24 5 25 1%27 stroke=%27%237aa86a%27 stroke-width=%272.4%27 fill=%27none%27 stroke-linecap=%27round%27/%3E%3Cpath d=%27M37 48 C33 33 41 29 37 15 C34 8 37 5 38 1%27 stroke=%27%234f7d46%27 stroke-width=%272.4%27 fill=%27none%27 stroke-linecap=%27round%27/%3E%3C/svg%3E')",
            backgroundRepeat: 'repeat',
            backgroundPosition: 'top left',
            backgroundSize: '46px 48px',
        }
    if (normalized.includes('summer forest'))
        return {
            background:
                "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2756%27 height=%2756%27%3E%3Cpath d=%27M4 24 L13 6 L22 24 Z%27 fill=%27%232c6238%27/%3E%3Cpath d=%27M30 40 L39 22 L48 40 Z%27 fill=%27%23357544%27/%3E%3C/svg%3E'), linear-gradient(160deg,#2f6b3f 0%,#16462a 100%)",
            backgroundRepeat: 'repeat',
            backgroundPosition: 'top left',
            backgroundSize: '56px 56px, cover',
        }

    return { background: '#ffffff' }
}

const flyerSwatchStyle = (value) => {
    const normalized = String(value || '').toLowerCase()

    if (normalized.includes('parchment')) {
        return { background: 'linear-gradient(135deg,#fbf3e0,#f1e4c6)' }
    }
    if (normalized.includes('abstract')) {
        return {
            background:
                'conic-gradient(from 200deg at 32% 30%,#f6d9c2,#fde7c0,#e7eef7,#f3dfe9,#f6d9c2)',
        }
    }
    if (normalized.includes('beach')) {
        return {
            background:
                'linear-gradient(180deg,#cfeaf4 0%,#e8f3ee 50%,#f4e7c6 51%,#efddb6 100%)',
        }
    }
    if (normalized.includes('flag')) {
        return {
            background:
                'linear-gradient(135deg,#ccd8ef 0 38%,rgba(0,0,0,0) 38%) top left / 42% 52% no-repeat, repeating-linear-gradient(180deg,#f6e3e0 0 9px,#fff 9px 18px)',
        }
    }
    if (normalized.includes('woodgrain')) {
        return {
            background:
                'repeating-linear-gradient(92deg,#e7cfa8 0 7px,#dcc096 7px 12px,#e3c9a0 12px 19px)',
        }
    }

    return { background: 'linear-gradient(135deg,#fbf3e0,#f1e4c6)' }
}

const AppearanceSection = forwardRef(function AppearanceSection(props, ref) {
    const {
        item,
        getValues,
        watch,
        setValue,
        defaultCustomizationTemplate,
        errors,
        product,
        ...restProps
    } = props

    const sourceAppearanceFields = Array.isArray(item?.fields)
        ? item.fields
        : []
    const seenFieldNames = new Set()
    const appearanceFields = sourceAppearanceFields.filter((field) => {
        if (!field?.name || seenFieldNames.has(field.name)) return false
        seenFieldNames.add(field.name)
        return true
    })
    const isLetterAppearance =
        isPersonalLetterProduct(product) ||
        isProfessionalLetterProduct(product) ||
        Boolean(
            product?.slug &&
                (String(product.slug).includes('personal-letters') ||
                    String(product.slug).includes('professional-letters')),
        )
    const fields = isLetterAppearance
        ? orderLetterAppearanceFields(appearanceFields)
        : appearanceFields
    if (watch) watch(fields.map((field) => field.name))
    const addModal = ModalStore((state) => state.add)
    const duplicatePreviousOrderValue = watch
        ? watch(duplicatePreviousOrderFieldName)
        : getValues?.(duplicatePreviousOrderFieldName)
    const isAppearanceLocked = Boolean(duplicatePreviousOrderValue)

    if (fields.length < 1) return null

    return (
        <div
            ref={ref}
            data-appearance-section="true"
            className="mb-2 rounded-xl border border-[#E1DACE] bg-white px-4 py-3 transition-all duration-150">
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold leading-5 text-olm-black">
                        {item.label || 'Appearance'}
                    </span>
                    {item.tooltipText && (
                        <button
                            type="button"
                            tabIndex={0}
                            aria-label={`More information about ${
                                item.label || 'Appearance'
                            }`}
                            className="group/info relative inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] border-gray-400 bg-transparent p-0 font-serif text-[10px] font-bold italic leading-none text-gray-400 hover:border-[#ED5C2F] hover:text-[#ED5C2F] focus:border-[#ED5C2F] focus:text-[#ED5C2F] focus:outline-none">
                            i
                            <span className="pointer-events-none absolute bottom-[150%] right-[-3px] z-[60] w-[230px] rounded-lg bg-[#1A1D23] px-3 py-2 text-left font-sans text-[11.5px] font-medium not-italic leading-[1.45] text-white opacity-0 shadow-[0_12px_34px_rgba(26,29,35,0.18)] transition-opacity duration-150 group-hover/info:opacity-100 group-focus/info:opacity-100">
                                {item.tooltipText}
                                <span className="absolute right-[9px] top-full h-0 w-0 border-[5px] border-transparent border-t-[#1A1D23]" />
                            </span>
                        </button>
                    )}
                </div>
                {isAppearanceLocked && (
                    <p className="mt-2 text-[12.5px] font-medium leading-[1.45] text-gray-500">
                        Appearance is copied from your original order. Uncheck
                        &quot;Duplicate this order&quot; to make changes.
                    </p>
                )}
            </div>

            <div className="mt-4 flex flex-col gap-3 border-t border-dashed border-[#E1DACE] pt-4">
                {fields.map((field) => {
                    const watchedValue = watch ? watch(field.name) : undefined
                    const value = getDisplayValue(
                        field,
                        getValues,
                        defaultCustomizationTemplate,
                        watchedValue,
                    )
                    const fontClass = findFont(value)
                    const hasError = Boolean(errors?.[field.name])
                    const isEnvelopeField =
                        field.name.includes('Envelope Style') ||
                        field.name.includes(envelopeOptionFieldName)
                    const normalizedLabel = normalizeLabel(
                        field,
                        isLetterAppearance,
                    )
                    const fontType = getAppearanceFontType(
                        field,
                        isLetterAppearance,
                    )
                    const isFontStyleField = Boolean(fontType)
                    const isHandwritingStyleField =
                        normalizedLabel === 'Handwriting style'
                    const isEnvelopeStyleField =
                        normalizedLabel === 'Envelope Style'
                    const isPostcardStyleField =
                        normalizedLabel === 'Postcard style'
                    const isFlyerStyleField = normalizedLabel === 'Flyer style'

                    return (
                        <div key={field.name}>
                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">
                                {normalizeLabel(field, isLetterAppearance)}
                            </label>
                            {isFontStyleField ||
                            isHandwritingStyleField ||
                            isEnvelopeStyleField ||
                            isPostcardStyleField ||
                            isFlyerStyleField ? (
                                <button
                                    type="button"
                                    disabled={isAppearanceLocked}
                                    onClick={() => {
                                        if (isAppearanceLocked) return
                                        addModal(
                                            isFontStyleField ||
                                                isHandwritingStyleField ? (
                                                <FontStyleModal
                                                    item={field}
                                                    title={
                                                        fontType === 'envelope'
                                                            ? 'Envelope Font'
                                                            : fontType ===
                                                              'letter'
                                                            ? 'Letter Font'
                                                            : 'Font Style'
                                                    }
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                    afterSelection={() => null}
                                                    {...restProps}
                                                />
                                            ) : isFlyerStyleField ? (
                                                <FlyerStyleModal
                                                    item={field}
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                    afterSelection={() => null}
                                                    {...restProps}
                                                />
                                            ) : isPostcardStyleField ? (
                                                <PostcardStyleModal
                                                    item={field}
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                    afterSelection={() => null}
                                                    {...restProps}
                                                />
                                            ) : (
                                                <EnvelopeStyleModal
                                                    item={field}
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                    afterSelection={() => null}
                                                    product={product}
                                                    {...restProps}
                                                />
                                            ),
                                        )
                                    }}
                                    className={classNames(
                                        hasError
                                            ? 'border-red-500'
                                            : 'border-[#E1DACE]',
                                        isAppearanceLocked
                                            ? 'cursor-not-allowed bg-gray-100 text-gray-400 opacity-80'
                                            : 'bg-white text-olm-black hover:border-[#F28230] focus:ring-4 focus:ring-[#F28230]/15',
                                        'flex w-full items-center gap-3 rounded-[10px] border px-[13px] py-[11px] text-left text-[14.5px] transition-all duration-150 focus:outline-none',
                                    )}>
                                    {(isEnvelopeField ||
                                        isPostcardStyleField ||
                                        isFlyerStyleField) && (
                                        <span
                                            className={classNames(
                                                'h-[23px] w-[36px] flex-shrink-0 rounded-[4px] border border-[rgba(0,0,0,0.08)]',
                                            )}
                                            style={
                                                isEnvelopeField
                                                    ? envelopeSwatchStyle(
                                                          value,
                                                          product,
                                                      )
                                                    : isPostcardStyleField
                                                    ? postcardSwatchStyle(value)
                                                    : isFlyerStyleField
                                                    ? flyerSwatchStyle(value)
                                                    : undefined
                                            }
                                        />
                                    )}
                                    <span
                                        className={classNames(
                                            fontClass,
                                            isAppearanceLocked
                                                ? 'text-gray-500'
                                                : '',
                                            'min-w-0 flex-1 truncate font-medium',
                                        )}>
                                        {value || normalizedLabel}
                                    </span>
                                    <SelectCaret className="ml-auto" />
                                </button>
                            ) : (
                                <div className="relative">
                                    {isEnvelopeField && (
                                        <span
                                            className="pointer-events-none absolute left-[13px] top-1/2 z-10 h-[23px] w-[36px] -translate-y-1/2 rounded-[4px] border border-[rgba(0,0,0,0.08)]"
                                            style={envelopeSwatchStyle(
                                                value,
                                                product,
                                            )}
                                        />
                                    )}
                                    <select
                                        disabled={isAppearanceLocked}
                                        value={value}
                                        onChange={(event) =>
                                            setValue(
                                                field.name,
                                                event.target.value,
                                            )
                                        }
                                        style={{
                                            appearance: 'none',
                                            WebkitAppearance: 'none',
                                            MozAppearance: 'none',
                                            backgroundImage: 'none',
                                        }}
                                        className={classNames(
                                            hasError
                                                ? 'border-red-500'
                                                : 'border-[#E1DACE]',
                                            isAppearanceLocked
                                                ? 'cursor-not-allowed bg-gray-100 text-gray-500 opacity-80'
                                                : 'bg-white text-olm-black focus:border-[#F28230] focus:ring-4 focus:ring-[#F28230]/15',
                                            fontClass,
                                            isEnvelopeField
                                                ? 'pl-[59px]'
                                                : 'pl-[13px]',
                                            'w-full appearance-none rounded-[10px] border py-[11px] pr-10 text-left text-[14.5px] font-medium transition-all duration-150 focus:outline-none',
                                        )}>
                                        {Array.isArray(field.options) &&
                                            field.options.map((option) => (
                                                <option
                                                    key={option}
                                                    value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                    </select>
                                    <SelectCaret className="pointer-events-none absolute right-[14px] top-1/2 -translate-y-1/2" />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
})

export default AppearanceSection
